import { useAuth0 } from '@auth0/auth0-react';
import { subject } from '@casl/ability';
import {
    faPen,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import TableCell, {
    tableCellClasses,
} from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, {
    useContext,
    useState,
} from 'react';
import { toast } from 'react-toastify';

import { getAuthToken } from '../../auth/authHelpers';
import { Can } from '../../auth/Can';
import {
    POL_THEME,
    ThemeContext,
} from '../../constants/theme';
import customerDataRequest from '../../services/api/customer-data';
import { cacheInvalidation } from '../../utils/cacheInvalidation';
import { getDaysInOrder } from '../../utils/days';

import MassTimeAddEdit, {
    ADD_TIME,
    EDIT_TIME,
} from './MassTimeAddEdit';

import styles from './MassTimesView.scss';

const stylesProps = {
    tableProps: {
        width: '89.6px',
        font: 'normal normal normal 12px/40px "Open Sans"',
        color: '#3C3B3B',
        padding: '2px',
        paddingLeft: '10px',
        textAlign: 'left',
    },
    btnProps: {
        font: 'normal normal normal 14px/19px "Open Sans"',
        textAlign: 'left',
        letterSpacing: '0px',
    },
    cancelBtnProps: {
        ml: 1.0,
        mt: 0.5,
        color: '#264550',
    },
    addBtnProps: {
        backgroundColor: '#264550',
        color: '#FFFFFF',
    },
    addTimeBtnProps: {
        backgroundColor: '#264550',
        top: '100%',
        height: 30,
        float: 'right',
        position: 'relative',
        transform: 'translateY(50%)',
        color: '#FFFFFF',
    },
};

const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: stylesProps.tableProps,
    [`&.${tableCellClasses.body}`]: stylesProps.tableProps,
}));

const MassTimeView = ({
    getMassTimes,
    isLoadingMassTime,
    massTimesEntries,
    salesforceId,
    isHidden,
}) => {
    const themeContext = useContext(ThemeContext);
    const { getAccessTokenSilently } = useAuth0();
    const [isAddTimeActive, setIsAddTimeActive] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState({});

    const handleAddTimeAction = () => {
        setIsAddTimeActive(true);
        setSelectedRowData({ action: ADD_TIME });
    };

    const handleEditAction = (row) => {
        setIsAddTimeActive(true);
        setSelectedRowData({ ...row, action: EDIT_TIME });
    };

    const massTimeCallback = async (token = null) => {
        setIsAddTimeActive(false);

        if (token) {
            await cacheInvalidation(`/organizations/${salesforceId}/mass-times`, token);
            await getMassTimes();
        }
    };

    const deleteMassTime = async (massTimeId) => {
        const token = await getAuthToken(getAccessTokenSilently);

        try {
            await customerDataRequest('delete', `/organizations/${salesforceId}/mass-times/${massTimeId}`, token);
            toast.success(`${themeContext === POL_THEME ? 'Mass Times' : 'Hours'} Deleted`);
        } catch (error) {
            toast.error(`There was an error deleting ${themeContext === POL_THEME ? 'Mass Times' : 'Hours'} details.`);
            throw error;
        }

        await massTimeCallback(token);
    };

    const getFormattedTime = (time) => DateTime.fromFormat(time, 'h:mm:ss').toFormat('h:mm a');

    const displayMassTimes = (daysType, data) => {
        const sortedDataByDays = getDaysInOrder(data, 'day_of_week');
        return (
            <div className={styles.hoursContainer}>
                <h3>{daysType}</h3>
                <TableContainer component={Paper}>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow key="headerRow">
                                <StyledTableCell
                                    key="day"
                                    size="medium"
                                    align="left"
                                >
                                    Day
                                </StyledTableCell>
                                <StyledTableCell
                                    key="time"
                                    align="right"
                                >
                                    Time
                                </StyledTableCell>
                                <StyledTableCell
                                    key="languages"
                                    align="right"
                                >
                                    Language
                                </StyledTableCell>
                                <StyledTableCell
                                    key="details"
                                    align="right"
                                >
                                    Details
                                </StyledTableCell>
                                <StyledTableCell
                                    key="delete"
                                    align="right"
                                />
                                <StyledTableCell
                                    key="edit"
                                    align="right"
                                />
                            </TableRow>
                        </TableHead>
                        <TableBody
                            sx={{
                                l: 100,
                            }}
                        >
                            {sortedDataByDays.map((row) => (
                                <TableRow key={row.id}>
                                    <StyledTableCell
                                        key="dayOfWeek"
                                        component="th"
                                        scope="row"
                                    >
                                        {row.day_of_week}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        key="startTime"
                                        align="right"
                                    >
                                        {getFormattedTime(row.start_time)}
                                        {row.end_time && `-${getFormattedTime(row.end_time)}`}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        key="languages"
                                        align="right"
                                    >
                                        {row.languages}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        key="additionalDetails"
                                        align="right"
                                    >
                                        {row.additional_details}
                                    </StyledTableCell>
                                    <Can I="admin" this={subject('claimed', { id: salesforceId })}>
                                        <StyledTableCell
                                            key="deleteIcons"
                                            sx={{
                                                '&.MuiTableCell-body': {
                                                    textAlign: 'right',
                                                    width: '5%',
                                                },
                                            }}
                                        >
                                            <button
                                                className={styles.removeBtnStyles}
                                                onClick={() => deleteMassTime(row.id)}
                                                type="button"
                                                aria-label="Delete"
                                                disabled={isHidden}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                />
                                                {' '}
                                            </button>
                                        </StyledTableCell>
                                        <StyledTableCell
                                            key="editIcons"
                                            sx={{
                                                '&.MuiTableCell-body': {
                                                    textAlign: 'right',
                                                    width: '5%',
                                                },
                                            }}
                                        >
                                            <button
                                                className={styles.removeBtnStyles}
                                                onClick={() => handleEditAction(row)}
                                                type="button"
                                                aria-label="Edit"
                                                disabled={isHidden}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faPen}
                                                />
                                            </button>
                                        </StyledTableCell>
                                    </Can>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        );
    };

    const generateMassTimesTables = () => {
        const massTimeTables = Object.keys(massTimesEntries).map((key) => (
            <div key={key} className={styles.timesContainer}>
                {displayMassTimes(key, massTimesEntries[key])}
            </div>
        ));
        return massTimeTables;
    };

    const massTimeTableDisplay = () => (
        <>
            <h2>
                {' '}
                {themeContext === POL_THEME ? 'Mass Times' : 'Hours'}
                {' '}
            </h2>
            <Divider />
            <Can I="admin" this={subject('claimed', { id: salesforceId })}>
                <Box>
                    <Button
                        variant="contained"
                        sx={{
                            ...stylesProps.btnProps,
                            ...stylesProps.addTimeBtnProps,
                        }}
                        onClick={() => handleAddTimeAction()}
                        disabled={isHidden}
                    >
                        Add Time
                    </Button>
                </Box>
            </Can>
            {!isLoadingMassTime && !!massTimesEntries && (
                <>{generateMassTimesTables()}</>
            )}
        </>
    );

    // return output for parent component
    return isAddTimeActive
        ? (
            <MassTimeAddEdit
                formInputProps={selectedRowData}
                formSubmitCallback={massTimeCallback}
                orgSalesforceId={salesforceId}
                isHidden={isHidden}
            />
        )
        : massTimeTableDisplay();
};

MassTimeView.propTypes = {
    getMassTimes: PropTypes.func.isRequired,
    isLoadingMassTime: PropTypes.bool,
    massTimesEntries: PropTypes.object,
    salesforceId: PropTypes.string.isRequired,
    isHidden: PropTypes.bool,
};

MassTimeView.defaultProps = {
    isLoadingMassTime: false,
    massTimesEntries: {},
    isHidden: false,
};

export default MassTimeView;
